import apiClient from './apiClient';
import screenwriterDB from '../storage/indexedDB';
import gitService from './gitService';
import localFileService from './localFileService';
import versionMergeInfoService from './versionMergeInfoService';
import { isElectron, isWebAggressiveSyncEnabled } from '../utils/electronBridge';

/**
 * Sync Service for IndexedDB <-> PostgreSQL synchronization
 * Handles offline-first operations with bi-directional sync
 * Includes git-based version control sync for multi-device support
 */

class SyncService {
  constructor() {
    this.isOnline = navigator.onLine;
    this.lastSyncTime = null;
    this.syncInProgress = false;
    this.lastSyncError = null;

    // Sync mode: 'cloud', 'local', or 'both'
    this.syncMode = localFileService.getSyncMode();

    // Setup online/offline listeners
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Load last sync time from localStorage
    this.lastSyncTime = localStorage.getItem('lastSyncTime')
      ? new Date(localStorage.getItem('lastSyncTime'))
      : null;

    // Version-history pushes queued by 'versionCommitted' (see
    // scheduleVersionHistoryPush), keyed by fileId:languageCode.
    this._versionPushTimers = new Map();
    document.addEventListener('versionCommitted', (event) => {
      const { fileId, languageCode } = event.detail || {};
      this.scheduleVersionHistoryPush(fileId, languageCode);
    });

    // Auto-sync interval (when online)
    this.setupAutoSync();
  }

  // Electron hybrid setup: write every file already in the local database
  // (files shared with the user go into a 'Shared with me' folder)
  // (e.g. everything just pulled from the server on a fresh install/login)
  // into the local project folder. Downloads only write files as they change,
  // so without this a folder chosen after the first pull would stay empty
  // until each file is next edited. Returns how many files were written.
  async mirrorAllToLocal() {
    if (!isElectron || localFileService.getSyncMode() === 'cloud' || !localFileService.getProjectFolder()) return 0;

    let written = 0;
    const files = (await screenwriterDB.listFiles()) || [];
    for (const file of files) {
      try {
        if (await localFileService.saveFileLocallyById(file.id)) written++;
      } catch (error) {
        console.warn(`SyncService: local mirror failed for file ${file.id} (non-fatal):`, error);
      }
    }
    console.log(`SyncService: mirrored ${written}/${files.length} files to the local project folder`);
    return written;
  }

  // True if ANY locally-held data might not be on the server yet. Deliberately
  // conservative: an unreadable store, a never-synced file (including a
  // plan-limited file that intentionally stays local-only), or any
  // not-yet-synced language/chat/note all count as pending, so the web
  // cache reset below is skipped rather than risking unsent edits.
  async hasPendingLocalChanges() {
    const isNewerThanSync = (row) => {
      const synced = row.last_synced_at || row.last_synced;
      if (!synced) return true;
      const modified = row.local_updated_at || row.last_modified;
      return modified ? new Date(modified) > new Date(synced) : false;
    };

    try {
      const files = (await screenwriterDB.listFiles()) || [];
      for (const file of files) {
        if (file.is_shared) continue; // owned by someone else; nothing of ours to lose
        if (isNewerThanSync(file)) return true;
        const languages = (await screenwriterDB.getFileLanguages(file.id)) || [];
        if (languages.some(isNewerThanSync)) return true;
      }

      const unsyncedChats = (await screenwriterDB.getUnsyncedChatHistories()) || [];
      if (unsyncedChats.length > 0) return true;

      const notes = (await screenwriterDB.listStandaloneNotes()) || [];
      if (notes.some((note) => note.syncStatus && note.syncStatus !== 'synced')) return true;

      return false;
    } catch (error) {
      console.warn('SyncService: could not verify pending local changes, assuming there are some:', error);
      return true;
    }
  }

  // Web only (per openspec/changes/web-server-source-of-truth): the server is
  // the only durable copy, so once per browser-tab session -- after login or
  // app load -- push anything pending, and only if the server confirms
  // everything is up to date, drop the local IndexedDB cache so it can't go
  // stale across sessions; the normal initial sync below then rebuilds it
  // fresh from the server (the same path a brand-new browser takes). Any
  // doubt (offline, sync error, still-pending data) keeps the cache: unsent
  // edits are never destroyed. Electron is untouched -- its local copy is
  // its offline store.
  async resetWebCacheIfFullySynced() {
    if (isElectron || !isWebAggressiveSyncEnabled) return false;
    if (!this.isOnline || !this.authToken) return false;

    let userId = null;
    try { userId = JSON.parse(localStorage.getItem('currentUser') || 'null')?.id; } catch { /* ignore */ }
    const resetKey = 'tarrWebCacheResetFor';
    try {
      if (sessionStorage.getItem(resetKey) === String(userId)) return false;
    } catch { /* sessionStorage unavailable: fall through, worst case we re-check */ }

    if (await this.hasPendingLocalChanges()) {
      await this.performFullSync();
      if (this.lastSyncError || await this.hasPendingLocalChanges()) {
        console.log('SyncService: web cache kept -- local data not fully confirmed on server');
        return false;
      }
    }

    try {
      await screenwriterDB.deleteDatabase();
      this.lastSyncTime = null;
      localStorage.removeItem('lastSyncTime');
      try { sessionStorage.setItem(resetKey, String(userId)); } catch { /* best effort */ }
      console.log('SyncService: web IndexedDB cleared after confirmed sync; rebuilding from server');
      return true;
    } catch (error) {
      console.warn('SyncService: web cache reset failed (non-fatal, cache kept):', error);
      return false;
    }
  }

  // Initialize sync service
  async initialize() {
    console.log('SyncService: Initializing...');

    // Auth is cookie-based — authenticated if currentUser exists in localStorage
    this.authToken = !!localStorage.getItem('currentUser');

    // apiClient handles baseURL + withCredentials automatically (cookie-based auth)

    // Initial sync if online and authenticated
    if (this.isOnline && this.authToken) {
      // Web only: start each browser-tab session from a fresh, server-derived
      // cache (no-op on Electron, and skipped whenever anything is unsent).
      await this.resetWebCacheIfFullySynced();

      // Check if local database is empty (fresh browser/incognito)
      const localFiles = await screenwriterDB.listFiles();
      const hasLocalFiles = localFiles && localFiles.length > 0;

      const timeSinceLastSync = this.lastSyncTime ? (Date.now() - this.lastSyncTime.getTime()) : Infinity;

      // Force sync if no local files OR if more than 5 minutes since last sync or never synced
      if (!hasLocalFiles || !this.lastSyncTime || timeSinceLastSync > 5 * 60 * 1000) {
        if (!hasLocalFiles) {
          console.log('SyncService: No local files detected, performing initial sync from server');
        } else {
          console.log('SyncService: Performing scheduled full sync');
        }
        await this.performFullSync();
      } else {
        console.log('SyncService: Skipping initial sync - synced recently and have local files');
      }
    }
  }

  // Handle online event
  async handleOnline() {
    console.log('SyncService: Device came online');
    this.isOnline = true;

    // Sync pending operations if authenticated
    if (this.authToken) {
      await this.performFullSync();
    }
  }

  // Handle offline event
  handleOffline() {
    console.log('SyncService: Device went offline');
    this.isOnline = false;
  }

  // Setup auto-sync interval
  setupAutoSync() {
    console.log('SyncService: Auto-sync ENABLED with timestamp-based conflict resolution');

    // Sync every 60 seconds when online and authenticated (increased from 30s to reduce frequency)
    setInterval(() => {
      if (this.isOnline && !this.syncInProgress && this.authToken && !this.isUserActivelyEditing()) {
        // Coordinate with analytics service - don't sync if analytics is syncing
        const analyticsService = window.__analyticsService;
        if (analyticsService?.isSyncing) {
          console.log('SyncService: Skipping incremental sync - analytics is syncing');
          return;
        }
        this.performIncrementalSync();
      }
    }, 60000); // 60 seconds instead of 30

    // Full sync every 10 minutes when online and authenticated (increased from 5min to reduce frequency)
    setInterval(() => {
      if (this.isOnline && !this.syncInProgress && this.authToken && !this.isUserActivelyEditing()) {
        // Coordinate with analytics service - don't sync if analytics is syncing
        const analyticsService = window.__analyticsService;
        if (analyticsService?.isSyncing) {
          console.log('SyncService: Skipping full sync - analytics is syncing');
          return;
        }
        this.performFullSync();
      }
    }, 10 * 60 * 1000); // 10 minutes instead of 5
  }

  // Check if user is actively editing (has unsaved changes or recent activity).
  // Per openspec/changes/web-server-source-of-truth: whenever the app isn't
  // in local-only sync mode (web is always non-local; Electron defaults to
  // 'both'), the debounced/flush-on-close push (webContentPushService,
  // wired into ProseMirrorEditor's handleAutoSave) already keeps the server
  // reasonably current during active editing, so gating the periodic timers
  // on this check would otherwise defer BOTH content sync and
  // gitSyncAllFiles's push of already-made local version-history commits for
  // as long as the user keeps typing (previously effectively unbounded) --
  // capping that to the real 60s/10min cadence is a straightforward
  // improvement, not a trade-off. Only genuine local-only mode (explicit
  // offline-only opt-out, Electron only) keeps this check as before, since
  // that mode has no separate debounced push to fall back on.
  isUserActivelyEditing() {
    if (isWebAggressiveSyncEnabled && localFileService.getSyncMode() !== 'local') return false;

    // Check if there are unsaved changes in any open files
    const hasUnsavedChanges = window.document.dispatchEvent ? (() => {
      const event = new CustomEvent('checkUnsavedChanges');
      document.dispatchEvent(event);
      return event.detail?.hasUnsaved || false;
    })() : false;

    // Check if user has been active in the last 10 seconds
    const lastActivity = localStorage.getItem('lastEditorActivity');
    const isRecentActivity = lastActivity ? (Date.now() - parseInt(lastActivity)) < 10000 : false;

    return hasUnsavedChanges || isRecentActivity;
  }

  // Check if there are active collaborators for a file
  async hasActiveCollaborators(fileId) {
    if (!this.isOnline || !this.authToken) {
      return false;
    }

    try {
      const response = await apiClient.get(`/api/collaboration/activity/${fileId}`);
      const { activeSessions } = response;
      return activeSessions && activeSessions.length > 0;
    } catch (error) {
      if (error.response?.status !== 403) {
        console.warn('Failed to check active collaborators:', error.message);
      }
      return false;
    }
  }

  // Check if file is shared with other users
  async isSharedFile(fileId) {
    try {
      const response = await apiClient.get(`/api/collaboration/file/${fileId}`);
      const collaborations = response;
      return collaborations && collaborations.length > 0;
    } catch (error) {
      console.warn('Failed to check if file is shared:', error.message);
      return false;
    }
  }

  // Whether the user has opted into "AI edits apply directly to the editor
  // without review" (useEditorPreferences.js's autoEditMode). Read directly
  // from the same localStorage blob that hook persists to rather than
  // threading the preference through every resolveOfflineConflict caller --
  // this is the population actually exposed to a server-side AI/MCP edit
  // racing an open tab, so conflicts for them get the same real merge a
  // collaborated file already gets instead of blind last-write-wins.
  _isAutoEditModeEnabled() {
    try {
      const stored = localStorage.getItem('screenwriter_preferences');
      return !!(stored && JSON.parse(stored).autoEditMode);
    } catch (error) {
      return false;
    }
  }

  // Enhanced conflict resolution for offline scenarios
  async resolveOfflineConflict(conflictData) {
    const {
      fileId,
      fileName,
      localFile,
      serverFile,
      localModified,
      serverModified,
      _lastSynced,
      multilingualContent
    } = conflictData;

    try {
      // Every conflicting file is merged with git (a real merge, recorded as a
      // new version), not resolved by picking whichever side has the newer
      // timestamp -- that silently throws away the other side's edits.
      const isShared = await this.isSharedFile(fileId);

      {
        console.log(`🔀 Git merge for conflicting file: ${fileName}${isShared ? ' (shared)' : ''}`);

        const versionHistoryService = (await import('./versionHistoryService.js')).default;

        const mergeResults = [];
        const languages = multilingualContent || [{ language_code: 'en', content: localFile.content }];

        for (const localLang of languages) {
          // The server sends camelCase rows (languageCode); older paths used snake_case.
          const serverLang = (serverFile.fileLanguages || []).find(
            l => (l.languageCode || l.language_code) === localLang.language_code
          );

          if (!serverLang) {
            // Language doesn't exist on server, upload it
            mergeResults.push({
              language_code: localLang.language_code,
              success: true,
              content: localLang.content,
              strategy: 'local_only'
            });
            continue;
          }

          const mergeResult = await versionHistoryService.mergeWithServer(
            fileId,
            localLang.content,
            serverLang.content,
            localLang.language_code
          );

          mergeResults.push({
            language_code: localLang.language_code,
            ...mergeResult
          });
        }

        // Check if all merges succeeded
        const allSuccess = mergeResults.every(r => r.success);

        if (allSuccess) {
          console.log('✅ Git auto-resolved all conflicts');

          // Build merged file
          const mergedFile = {
            ...localFile,
            fileLanguages: mergeResults.map(r => ({
              language_code: r.language_code,
              content: r.content
            }))
          };

          // Keep the merged result locally too: the language sync that follows
          // then sees local and server agree (or local newer) instead of
          // conflicting over the same edits again.
          for (const r of mergeResults) {
            if (r.strategy === 'local_only') continue;
            const localLang = languages.find(l => l.language_code === r.language_code);
            try {
              await screenwriterDB.saveFileInLanguage(
                fileId,
                r.language_code,
                r.content,
                localLang?.title_page || null,
                { annotations: localLang?.annotations, pinned_locations: localLang?.pinned_locations }
              );
            } catch (saveError) {
              console.warn('SyncService: could not save merged content locally:', saveError.message);
            }
          }

          // Tag each merged commit so Version History can show it wasn't
          // just a normal edit -- best-effort, never blocks the upload.
          const connectedUsers = await this._getConnectedUsers(fileId);
          for (const r of mergeResults) {
            if (r.commit) {
              versionMergeInfoService
                .tagMerge(fileId, r.commit, r.language_code, 'auto_merge', connectedUsers)
                .catch(err => console.warn('SyncService: Failed to tag auto-merge commit:', err.message));
            }
          }

          return {
            operations: [{
              type: 'upload',
              file: mergedFile,
              conflict: true,
              reason: 'git_auto_merged'
            }],
            conflict: {
              fileId,
              fileName,
              resolution: 'git_auto_merged',
              localModified,
              serverModified,
              resolved: true,
              mergeStrategy: 'git_merge_with_server'
            }
          };
        } else {
          // Manual resolution required
          console.log('⚠️ Git merge conflicts - manual resolution required');

          this.storeConflictForResolution({
            fileId,
            fileName,
            localContent: multilingualContent,
            serverContent: serverFile.fileLanguages || [],
            localModified,
            serverModified,
            mergeResults
          });

          return {
            operations: [],
            conflict: {
              fileId,
              fileName,
              resolution: 'requires_manual_merge',
              localModified,
              serverModified,
              resolved: false,
              action: 'show_merge_ui',
              mergeResults
            }
          };
        }
      }
    } catch (error) {
      console.error('Error in enhanced conflict resolution:', error);

      // Fallback to simple timestamp resolution
      const serverIsNewer = serverModified > localModified;
      return {
        operations: [{
          type: serverIsNewer ? 'download' : 'upload',
          file: serverIsNewer ? serverFile : localFile,
          conflict: true,
          reason: 'fallback_timestamp'
        }],
        conflict: {
          fileId,
          fileName,
          resolution: 'fallback_' + (serverIsNewer ? 'server_wins' : 'local_wins'),
          localModified,
          serverModified,
          resolved: true
        }
      };
    }
  }

  // Snapshot of who's currently connected to a file (for merge tagging --
  // see resolveOfflineConflict). Best-effort: degrades to [] rather than
  // failing the merge/upload it's called from.
  async _getConnectedUsers(fileId) {
    try {
      const data = await apiClient.get(`/api/collaboration/activity/${fileId}`);
      const activeSessions = (data?.activeSessions || []).filter(s => !s.leftAt);
      const seen = new Map();
      for (const s of activeSessions) {
        if (s.user?.id && !seen.has(s.user.id)) {
          seen.set(s.user.id, {
            id: s.user.id,
            name: [s.user.firstName, s.user.lastName].filter(Boolean).join(' ') || s.user.email || 'Unknown'
          });
        }
      }
      return Array.from(seen.values());
    } catch (e) {
      console.warn('SyncService: Failed to fetch connected users for merge tagging:', e.message);
      return [];
    }
  }

  // Bridge gitService.pull()'s real per-commit 3-way merge conflicts into the
  // same Sync Conflicts tab pipeline the whole-file branch-merge path
  // (resolveOfflineConflict) already uses -- these used to be silently
  // console.warn'd and dropped, so real per-edit conflicts were invisible to
  // users. filepath is `${fileId}/${languageCode}.txt` (see gitService.js);
  // ours/theirs are the two sides' plain content (not the marker-laden
  // merged.result), matching the shape ConflictResolutionModal expects.
  async _reportGitPullConflicts(conflictDetails) {
    if (!conflictDetails || conflictDetails.length === 0) return;

    for (const entry of conflictDetails) {
      try {
        const { filepath, ours, theirs } = entry;
        const slashIdx = filepath.indexOf('/');
        if (slashIdx === -1) continue;
        const fileId = filepath.slice(0, slashIdx);
        const languageCode = filepath.slice(slashIdx + 1).replace(/\.txt$/, '');

        let fileName = fileId;
        try {
          const localFile = await screenwriterDB.getFile(fileId);
          if (localFile?.name) fileName = localFile.name;
        } catch {
          // Fall back to fileId as the display name
        }

        this.storeConflictForResolution({
          fileId,
          fileName,
          localContent: [{ language_code: languageCode, content: ours }],
          serverContent: [{ language_code: languageCode, content: theirs }],
          localModified: new Date(),
          serverModified: new Date(),
          mergeResults: [{ language_code: languageCode, success: false }],
        });
      } catch (e) {
        console.error('SyncService: Failed to report git pull conflict for UI resolution:', e);
      }
    }
  }

  // Store conflict data for UI resolution
  storeConflictForResolution(conflictData) {
    // Store in localStorage for the UI to pick up. Dedupe on fileId +
    // language: without this, the same unresolved file re-conflicting on
    // every sync pass kept appending a fresh full copy of both sides'
    // content forever, which is what blew through localStorage's quota
    // (observed live: 34 straight QuotaExceededError failures) and, worse,
    // silently dropped the newest conflict from the UI once the quota was
    // hit since the catch around this call swallows the error.
    const conflicts = JSON.parse(localStorage.getItem('syncConflicts') || '[]');
    const newLangCode = conflictData.localContent?.[0]?.language_code;
    const deduped = conflicts.filter(c =>
      !(c.fileId === conflictData.fileId && c.localContent?.[0]?.language_code === newLangCode)
    );
    deduped.push({
      ...conflictData,
      timestamp: Date.now(),
      id: `conflict_${Date.now()}_${conflictData.fileId}`
    });

    try {
      localStorage.setItem('syncConflicts', JSON.stringify(deduped));
    } catch (e) {
      // Quota still exceeded even after dedupe (e.g. many distinct files
      // conflicting at once) -- drop the oldest half and retry once rather
      // than losing the newest conflict silently.
      console.warn('SyncService: syncConflicts storage full, pruning oldest entries', e);
      const pruned = deduped.slice(Math.ceil(deduped.length / 2));
      try {
        localStorage.setItem('syncConflicts', JSON.stringify(pruned));
      } catch (e2) {
        console.error('SyncService: Could not store conflict even after pruning', e2);
      }
    }

    // Dispatch event for UI notification
    document.dispatchEvent(new CustomEvent('syncConflictDetected', {
      detail: conflictData
    }));

    // Let an open Version History panel show it straight away.
    try { document.dispatchEvent(new Event('syncConflictsChanged')); } catch { /* non-browser */ }
  }

  setSyncMode(mode) {
    this.syncMode = mode;
    localFileService.setSyncMode(mode);
    console.log('SyncService: Sync mode changed to', mode);
  }

  getSyncMode() {
    return this.syncMode;
  }

  async saveToLocal(file) {
    if (this.syncMode === 'local' || this.syncMode === 'both') {
      await localFileService.saveFileLocally(file);
    }
  }

  saveToLocalDebounced(file) {
    if (this.syncMode === 'local' || this.syncMode === 'both') {
      localFileService.saveFileLocallyDebounced(file);
    }
  }

  // Sync every local (cloud-synced) file's git-based version history against
  // the real git server, one (fileId, languageCode) repo at a time -- each
  // repo is now a real, standalone git remote (see gitService.js), so there
  // is no more single global "the repo" to sync in one call. This also
  // replaces the old seedGitBaseline(): a repo with no local history yet is
  // handled transparently by gitService.pull()'s fast-forward-adopt path,
  // since the server lazily seeds a fresh bare repo from the current
  // FileLanguage.content on first authorized touch (see gitRepoStore.js) --
  // there's no separate "baseline" step to run first.
  //
  // Only one browser tab may run this at a time: gitService.js's git storage
  // (lightning-fs) is guarded by a cross-tab Web Locks API mutex, and two
  // tabs each independently looping through every file's sync can starve
  // each other for minutes (confirmed live -- "Mutex timeout" cascades).
  // Elect a single leader tab per sync pass using the same Web Locks API
  // primitive lightning-fs itself relies on; a tab that can't become leader
  // skips this pass entirely instead of queuing behind it -- the leader's
  // pass already covers the same files.
  async gitSyncAllFiles() {
    if (!window.navigator?.locks) {
      // No Web Locks API support (very old browser) -- run unprotected,
      // same behavior as before this existed.
      return this._gitSyncAllFilesImpl();
    }

    let result = { pulled: 0, pushed: 0, conflicts: false, conflictDetails: [] };
    await navigator.locks.request('screenwriter-git-sync-leader', { ifAvailable: true }, async (lock) => {
      if (!lock) {
        console.log('SyncService: Another tab is already running git sync -- skipping this pass');
        return;
      }
      result = await this._gitSyncAllFilesImpl();
    });
    return result;
  }

  async _gitSyncAllFilesImpl() {
    const result = { pulled: 0, pushed: 0, conflicts: false, conflictDetails: [] };

    const allLocalFiles = await screenwriterDB.listFiles();
    // Local-only files never touch the cloud at all (see syncFiles()'s
    // identical filter) -- they have no server-side FileLanguage row for a
    // bare repo to be seeded from.
    const files = allLocalFiles.filter(f => f.storageMode !== 'local');

    for (const file of files) {
      let languages;
      try {
        languages = await screenwriterDB.getFileLanguages(file.id);
      } catch (err) {
        console.warn(`SyncService: Could not read languages for file ${file.id}:`, err.message);
        continue;
      }
      if (!languages?.length) {
        languages = file.content ? [{ language_code: 'en', content: file.content }] : [];
      }

      for (const lang of languages) {
        const languageCode = lang.language_code || lang.languageCode || 'en';
        // lightning-fs guards its IndexedDB backend with a cross-tab Mutex
        // that can time out under the back-to-back git plumbing load of
        // syncing many (fileId, languageCode) repos in a tight loop -- one
        // retry after a short pause clears the transient case (an expiring/
        // contended lock), without masking a genuinely broken sync.
        let gitSyncResult;
        try {
          gitSyncResult = await gitService.sync(file.id, languageCode);
        } catch (err) {
          if (err.message !== 'Mutex timeout') {
            console.error(`SyncService: Git sync failed for file ${file.id}/${languageCode} (continuing with other files):`, err.message);
            continue;
          }
          console.warn(`SyncService: Git sync hit a Mutex timeout for file ${file.id}/${languageCode}, retrying once...`);
          await new Promise(resolve => setTimeout(resolve, 500));
          try {
            gitSyncResult = await gitService.sync(file.id, languageCode);
          } catch (retryErr) {
            console.error(`SyncService: Git sync retry failed for file ${file.id}/${languageCode} (continuing with other files):`, retryErr.message);
            continue;
          }
        }

        if (gitSyncResult.conflicts) {
          result.conflicts = true;
          result.conflictDetails.push(...(gitSyncResult.conflictDetails || []));
        } else {
          result.pulled += gitSyncResult.pulled || 0;
          result.pushed += gitSyncResult.pushed || 0;
        }
      }

      // Small stagger between files -- gives lightning-fs's own internal
      // lock lifecycle (acquire/debounced-release) room to settle instead of
      // hammering it back-to-back across many independent repos, which is
      // what produced the cascading Mutex timeouts above.
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    return result;
  }

  // Push a cloud file's new version-history commits to the server shortly
  // after they're made (debounced per file+language), instead of waiting for
  // the next 60s/10min pass -- the server should hold a file's history as
  // soon as its content. Local-only files (per-file storageMode 'local', or
  // the app-wide local-only mode) keep their history on the device only.
  scheduleVersionHistoryPush(fileId, languageCode = 'en', delayMs = 5000) {
    if (!fileId || !isWebAggressiveSyncEnabled) return;
    const key = `${fileId}:${languageCode}`;
    clearTimeout(this._versionPushTimers.get(key));
    this._versionPushTimers.set(key, setTimeout(() => {
      this._versionPushTimers.delete(key);
      this._pushVersionHistoryNow(fileId, languageCode);
    }, delayMs));
  }

  async _pushVersionHistoryNow(fileId, languageCode) {
    if (!this.isOnline || !this.authToken || localFileService.getSyncMode() === 'local') return;

    try {
      const file = await screenwriterDB.getFile(fileId);
      if (!file || file.storageMode === 'local') return;

      // Same cross-tab leader lock the periodic pass uses: lightning-fs is not
      // safe for two tabs' git plumbing at once. If a pass is already running
      // it covers this file too, so skipping is correct.
      const run = async () => {
        const result = await gitService.sync(fileId, languageCode);
        if (result?.conflicts) await this._reportGitPullConflicts(result.conflictDetails);
      };
      if (window.navigator?.locks) {
        await navigator.locks.request('screenwriter-git-sync-leader', { ifAvailable: true }, async (lock) => {
          if (lock) await run();
        });
      } else {
        await run();
      }
    } catch (error) {
      // Non-fatal: the commits stay in the local git store and go up with the
      // next periodic pass.
      console.warn(`SyncService: prompt version-history push failed for ${fileId}/${languageCode}:`, error?.message || error);
    }
  }

  // Perform full synchronization
  async performFullSync() {
    // Skip cloud sync if in local-only mode
    if (this.syncMode === 'local') {
      console.log('SyncService: Skipping cloud sync (local-only mode)');
      return;
    }

    if (this.syncInProgress || !this.isOnline || !this.authToken) return;

    console.log('SyncService: Starting full sync...');
    this.syncInProgress = true;

    try {
      // Sync files
      await this.syncFiles();

      // Git-based version control sync (multi-device), one repo per
      // (fileId, languageCode) -- see gitSyncAllFiles().
      try {
        console.log('SyncService: Starting git sync...');
        const gitSyncResult = await this.gitSyncAllFiles();
        console.log(`SyncService: Git sync complete - pulled ${gitSyncResult.pulled}, pushed ${gitSyncResult.pushed}`);
        if (gitSyncResult.conflicts) {
          console.warn('SyncService: Git sync detected conflicts - surfacing in Sync Conflicts tab');
          await this._reportGitPullConflicts(gitSyncResult.conflictDetails);
        }
      } catch (gitSyncError) {
        console.error('SyncService: Git sync failed but continuing with other syncs:', gitSyncError);
      }

      // Sync AI import jobs
      // AI import sync removed (import simplified to single API call)

      // Sync annotations
      await this.syncAnnotations();

      // Preferences are pushed per changed key by PreferencesStateProvider
      // (state/PreferencesStateContext.jsx), not as a whole blob on every full sync.

      // Sync document outlines
      await this.syncDocumentOutlines();

      // Sync chat history
      await this.syncChatHistory();

      // Sync version history (non-blocking - errors are logged but don't fail entire sync)
      try {
        await this.syncVersionHistory();
      } catch (versionSyncError) {
        console.error('SyncService: Version history sync failed but continuing with other syncs:', versionSyncError);
      }

      // Sync subscription info
      await this.syncSubscription();

      // Sync token usage to server
      await this.syncTokenUsage();

      // Update last sync time
      this.lastSyncTime = new Date();
      localStorage.setItem('lastSyncTime', this.lastSyncTime.toISOString());

      console.log('SyncService: Full sync completed successfully');
      this.lastSyncError = null; // Clear any previous errors
    } catch (error) {
      console.error('SyncService: Full sync failed:', error);
      this.lastSyncError = error.response?.data?.message || error.message || 'Sync failed';
    } finally {
      this.syncInProgress = false;
    }
  }

  // Perform incremental sync (only changes since last sync)
  async performIncrementalSync() {
    if (this.syncInProgress || !this.isOnline || !this.authToken || !this.lastSyncTime) {
      return this.performFullSync();
    }

    // Debug information
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const screenwriterUserId = localStorage.getItem('screenwriter_user_id');
    console.log('SyncService: Debug info:', {
      currentUser: currentUser,
      screenwriterUserId: screenwriterUserId,
      authToken: this.authToken ? 'present' : 'missing',
      lastSyncTime: this.lastSyncTime
    });

    console.log('SyncService: Starting incremental sync...');
    this.syncInProgress = true;

    try {
      // Get changes since last sync
      const response = await apiClient.get('/api/files/sync', {
        params: { lastSync: this.lastSyncTime.toISOString() }
      });

      const { serverChanges } = response || {};
      if (!Array.isArray(serverChanges)) return;

      // Apply server changes to local database
      for (const change of serverChanges) {
        await this.applyServerChange(change);
      }

      // Same gap syncFiles() had (see that function's own 'refreshFiles'
      // dispatch comment) -- applyServerChange writes last_synced/content
      // straight into IndexedDB via screenwriterDB.upsertFile/updateFile,
      // but this is the 60-SECOND incremental path (setupAutoSync's most
      // frequent timer), which never called syncFiles() at all -- it's a
      // fully separate write path applyServerChange() feeds directly.
      // Without this, only the 10-minute full sync (which does call
      // syncFiles()) or a manual "Sync now" ever refreshed MainSidebar's
      // sync badges; the far more common incremental tick left them stale
      // for up to 10 minutes even though the file was already synced.
      if (serverChanges.length > 0) {
        document.dispatchEvent(new CustomEvent('refreshFiles'));
      }

      // Git-based version control sync (multi-device), one repo per
      // (fileId, languageCode) -- see gitSyncAllFiles().
      try {
        console.log('SyncService: Starting git sync (incremental)...');
        const gitSyncResult = await this.gitSyncAllFiles();
        console.log(`SyncService: Git sync complete - pulled ${gitSyncResult.pulled}, pushed ${gitSyncResult.pushed}`);
        if (gitSyncResult.conflicts) {
          console.warn('SyncService: Git sync detected conflicts - surfacing in Sync Conflicts tab');
          await this._reportGitPullConflicts(gitSyncResult.conflictDetails);
        }
      } catch (gitSyncError) {
        console.error('SyncService: Git sync failed but continuing with incremental sync:', gitSyncError);
      }

      // Sync version history (non-blocking - errors are logged but don't fail entire sync)
      try {
        await this.syncVersionHistory();
      } catch (versionSyncError) {
        console.error('SyncService: Version history sync failed but continuing with incremental sync:', versionSyncError);
      }

      // Update last sync time
      this.lastSyncTime = new Date();
      localStorage.setItem('lastSyncTime', this.lastSyncTime.toISOString());

      console.log('SyncService: Incremental sync completed');
      this.lastSyncError = null; // Clear any previous errors
    } catch (error) {
      console.error('SyncService: Incremental sync failed:', error);
      this.lastSyncError = error.response?.data?.message || error.message || 'Sync failed';
    } finally {
      this.syncInProgress = false;
    }
  }

  // Sync files between local and server
  async syncFiles() {
    // Don't sync if not authenticated
    if (!this.authToken) {
      console.log('SyncService: Skipping file sync - not authenticated');
      return;
    }

    try {
      // Get local files - ensure database is initialized
      await screenwriterDB.init();

      // Ensure user context is set
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (currentUser.id) {
        screenwriterDB.setUserId(currentUser.id);
      }

      const allLocalFiles = await screenwriterDB.listFiles();
      // Filter out local-only files - they should never be synced to cloud
      const localFiles = allLocalFiles.filter(f => f.storageMode !== 'local');
      console.log('SyncService: Retrieved local files for sync:', localFiles.length, `(${allLocalFiles.length - localFiles.length} local-only skipped)`);

      // Get server files
      const response = await apiClient.get('/api/files', {
        params: { limit: 1000 }
      });
      const serverFiles = response?.files || [];

      // Sync file languages separately with proper timestamp-based resolution
      await this.syncFileLanguages(localFiles);

      // Create maps for easier comparison
      const localFileMap = new Map(localFiles.map(f => [f.id, f]));
      const serverFileMap = new Map(serverFiles.map(f => [f.id, f]));

      // No more dual ID mapping needed

      const conflicts = [];
      const operations = [];

      // Check for conflicts and needed operations
      for (const [fileId, localFile] of localFileMap) {
        const serverFile = serverFileMap.get(fileId);

        if (!serverFile) {
          // Check if file was previously synced to server. Falls back to
          // last_synced the same way MainSidebar's getFileSyncIndicator
          // already does -- a file whose local row was created before the
          // downloadFileFromServer "create new" branch fix (only last_synced
          // was ever set there, never last_synced_at) would otherwise read
          // as "never synced" here forever, even though it plainly was.
          const lastSynced = (localFile.last_synced_at || localFile.last_synced)
            ? new Date(localFile.last_synced_at || localFile.last_synced) : null;
          
          if (lastSynced) {
            // Local file was synced before, but is now missing from server -> it was deleted on server!
            console.log(`SyncService: Found local file deleted on server - deleting locally: ${localFile.name} (ID: ${fileId})`);
            operations.push({
              type: 'delete_local',
              file: localFile,
              reason: 'deleted_on_server'
            });
          } else {
            // Local file never synced -> upload
            operations.push({
              type: 'upload',
              file: localFile
            });
          }
        } else {
          // Compare timestamps and sync status for conflict resolution
          // First get base timestamp from main file
          let localModified = new Date(localFile.local_updated_at || localFile.last_modified);

          // Check multilingual content timestamps as they might be more recent
          const multilingualContent = await screenwriterDB.getFileLanguages(localFile.id);
          if (multilingualContent && multilingualContent.length > 0) {
            const multilingualTimestamps = multilingualContent
              .map(lang => new Date(lang.last_modified || lang.updated))
              .filter(date => !isNaN(date.getTime()));

            if (multilingualTimestamps.length > 0) {
              const latestMultilingualTime = new Date(Math.max(...multilingualTimestamps));
              if (latestMultilingualTime > localModified) {
                localModified = latestMultilingualTime;
                console.log(`Using multilingual timestamp for ${localFile.name}: ${latestMultilingualTime.toISOString()} (was ${new Date(localFile.last_modified).toISOString()})`);
              }
            }
          }

          const serverModified = new Date(serverFile.serverUpdatedAt || serverFile.updatedAt);
          // Same last_synced fallback as the missing-from-server branch above.
          const lastSynced = (localFile.last_synced_at || localFile.last_synced)
            ? new Date(localFile.last_synced_at || localFile.last_synced) : null;

          console.log(`Sync analysis for ${localFile.name}:`, {
            localModified: localModified.toISOString(),
            serverModified: serverModified.toISOString(),
            lastSynced: lastSynced?.toISOString(),
            localId: localFile.id,
            multilingualContentCount: multilingualContent?.length || 0
          });

          // Intelligent conflict resolution based on timestamps and content
          const localChangedSinceSync = lastSynced ? localModified > lastSynced : true;
          const serverChangedSinceSync = lastSynced ? serverModified > lastSynced : true;

          if (localChangedSinceSync && serverChangedSinceSync) {
            // Conflict detected - Enhanced resolution based on collaboration status
            console.log(`Conflict detected for file ${localFile.name}:`, {
              localModified: localModified.toISOString(),
              serverModified: serverModified.toISOString(),
              lastSynced: lastSynced?.toISOString()
            });

            const conflictResolution = await this.resolveOfflineConflict({
              fileId: localFile.id,
              fileName: localFile.name,
              localFile: localFile,
              serverFile: serverFile,
              localModified,
              serverModified,
              lastSynced,
              multilingualContent
            });

            operations.push(...conflictResolution.operations);
            conflicts.push(conflictResolution.conflict);
          } else if (localChangedSinceSync) {
            // Only local changed - upload
            operations.push({
              type: 'upload',
              file: localFile,
              reason: 'local_only_changed'
            });
          } else if (serverChangedSinceSync) {
            // Only server changed - download if server is newer
            if (serverModified > localModified) {
              console.log('Downloading server changes - server has newer timestamp');
              operations.push({
                type: 'download',
                file: serverFile,
                reason: 'server_only_changed_newer'
              });
            } else {
              console.log('Skipping server download - local timestamp is same or newer');
            }
          }
          // If neither changed since last sync, no operation needed — BUT still patch
          // project_id locally if the server has one and local doesn't.
          if (!localChangedSinceSync && !serverChangedSinceSync) {
            if (serverFile.projectId && !localFile.project_id) {
              console.log(`SyncService: Patching local project_id for ${localFile.name}`);
              // Explicitly preserve last_modified -- screenwriterDB.updateFile()
              // stamps it to "now" for any update that doesn't pass it explicitly,
              // which would falsely mark this file as locally-changed-since-sync
              // (and permanently "not synced" in the UI) even though nothing about
              // its actual content changed here.
              await screenwriterDB.updateFile(localFile.id, { project_id: serverFile.projectId, last_modified: localFile.last_modified });
            }
          }
        }
      }

      // Check for server files not in local
      for (const [fileId, serverFile] of serverFileMap) {
        if (!localFileMap.has(fileId)) {
          // A file missing locally is always downloaded, never inferred as "deleted
          // locally" from timestamps. `lastSyncTime` in localStorage isn't cleared on
          // logout and isn't tied to whether IndexedDB actually has data, so it can't
          // be trusted to distinguish a real local deletion from a fresh/evicted local
          // store — treating it as a deletion signal risks wiping files off the server
          // that the user never deleted. There's no local tombstone/deletion log, so
          // there's no safe basis for inferring server-side deletion here.
          console.log('SyncService: Found file missing locally - downloading:', serverFile.name, 'ID:', fileId);
          operations.push({
            type: 'download',
            file: serverFile
          });
        }
      }

      // Debug logging
      console.log('SyncService: Sync analysis:', {
        localFiles: localFiles.length,
        serverFiles: serverFiles.length,
        localFileIds: Array.from(localFileMap.keys()),
        serverFileIds: Array.from(serverFileMap.keys()),
        operations: operations.length
      });

      // Execute operations
      for (const operation of operations) {
        await this.executeFileOperation(operation);
      }

      // Every upload/download operation above stamps fresh last_synced/
      // last_synced_at (and possibly content) straight into IndexedDB via
      // screenwriterDB.updateFile/upsertFile -- but useFileStorage's `files`
      // state (what MainSidebar's getFileSyncIndicator actually reads) is
      // only ever populated once at mount and on this same 'refreshFiles'
      // event (see useFileStorage.js). Nothing in this function used to
      // dispatch it, so a background/manual sync would correctly update the
      // database yet leave the sidebar rendering its stale pre-sync
      // snapshot -- every synced file kept showing the "not synced" badge
      // until some unrelated action happened to trigger a reload. This is
      // the same event autoHandler.js already dispatches after its own
      // out-of-band syncFiles() call, for the identical reason.
      document.dispatchEvent(new CustomEvent('refreshFiles'));

      // Notify about conflicts that were resolved automatically
      if (conflicts.length > 0) {
        console.log(`SyncService: Resolved ${conflicts.length} conflicts automatically:`, conflicts);
        // Dispatch event for UI notification
        document.dispatchEvent(new CustomEvent('syncConflictsResolved', {
          detail: { conflicts, totalOperations: operations.length }
        }));
      }

      console.log(`SyncService: Synced ${operations.length} file operations`);
    } catch (error) {
      console.error('SyncService: File sync error:', error);
      // On 403/429 sync errors, emit syncLimited event instead of throwing
      const status = error.response?.status;
      if (status === 403 || status === 429) {
        const reason = status === 429 ? 'rate_limited' : 'forbidden';
        window.dispatchEvent(new CustomEvent('syncLimited', { detail: { reason, status } }));
        return; // Don't throw - handle gracefully
      }
      throw error;
    }
  }

  // Execute file sync operation
  async executeFileOperation(operation) {
    try {
      // Skip local-only files for all sync operations
      if (operation.file?.storageMode === 'local') {
        console.log('SyncService: Skipping sync operation for local-only file:', operation.file.name);
        return;
      }
      if (operation.type === 'upload') {
        // Check for active collaborators before uploading
        const hasActiveCollabs = await this.hasActiveCollaborators(operation.file.id);
        if (hasActiveCollabs) {
          console.log('SyncService: Skipping upload - active collaboration detected for file:', operation.file.name);
          return;
        }
        await this.uploadFileToServer(operation.file);
      } else if (operation.type === 'download') {
        await this.downloadFileFromServer(operation.file);
      } else if (operation.type === 'delete_local') {
        await this.deleteFileFromLocal(operation.file.id);
      }
    } catch (error) {
      // On 403/429 errors during file operations, emit syncLimited event
      const status = error.response?.status;
      if (status === 403 || status === 429) {
        const reason = status === 429 ? 'rate_limited' : 'forbidden';
        window.dispatchEvent(new CustomEvent('syncLimited', { detail: { reason, status, fileId: operation.file?.id } }));
        return;
      }
      console.error('SyncService: File operation error:', error);
    }
  }

  // Upload local file to server
  async uploadFileToServer(localFile) {
    try {
      // SKIP production sheets - they should be synced via production API, not files API
      // Handle template_id as either string or object
      const templateId = typeof localFile.template_id === 'string'
        ? localFile.template_id
        : (typeof localFile.template_id === 'object' && localFile.template_id !== null
          ? localFile.template_id.id
          : null);

      // 'sheet' is the current virtual-File template_id every production
      // entry gets (see server/routes/production.js's createVirtualFile) --
      // the other names below predate that convention and are kept in case
      // any pre-migration file still carries one. Without this check
      // matching 'sheet', this skip silently stopped applying to every
      // production entry, and this function went on to PUT a real files-API
      // update (wrong name/content, since a production entry's real content
      // lives in ProductionSheet/-Column/-Row/-Cell, not file_languages) for
      // every one of them on each sync pass -- confirmed live.
      if (templateId && (
        templateId === 'sheet' ||
        templateId.startsWith('production') ||
        templateId === 'callsheet' ||
        templateId === 'shootingschedule' ||
        templateId === 'budget' ||
        templateId === 'contactlist' ||
        templateId === 'locationlist' ||
        templateId === 'equipmentlist'
      )) {
        console.log('SyncService: Skipping upload - production sheets should use production API:', localFile.name);
        return;
      }

      // Get multilingual content for this file with annotations AND pinned locations
      const multilingualContent = [];
      if (localFile.available_languages) {
        for (const lang of localFile.available_languages) {
          try {
            const langData = await screenwriterDB.getFileInLanguage(localFile.id, lang);
            if (langData) {
              multilingualContent.push({
                languageCode: lang,
                content: langData.content,
                titlePage: langData.title_page,
                // Annotations are NOT uploaded with fileLanguages - they're synced separately
                pinnedLocations: langData.pinned_locations || {} // Include pinned locations from language data
              });
            }
          } catch (error) {
            console.warn(`Could not get content for language ${lang}:`, error);
          }
        }
      }

      // ALWAYS use multilingual storage as source of truth (never main file content)
      let actualContent = '';
      let actualTitlePage = {};

      if (multilingualContent.length > 0) {
        const defaultLangData = multilingualContent.find(lang => lang.languageCode === (localFile.default_language || 'en'));
        if (defaultLangData) {
          actualContent = defaultLangData.content || '';
          actualTitlePage = defaultLangData.titlePage || {};
          console.log(`SyncService: Using ONLY multilingual content (${defaultLangData.languageCode}):`, actualContent.length, 'chars, preview:', actualContent.substring(0, 50));
        } else {
          console.warn(`SyncService: No multilingual content found for default language ${localFile.default_language}`);
        }
      } else {
        console.warn(`SyncService: No multilingual content available for file ${localFile.name}. CAUTION: Using main file content as fallback.`);
        // Fallback to localFile.content if available (critical for recovery/sync of files not fully in DB)
        if (localFile.content) {
          actualContent = localFile.content;
          actualTitlePage = localFile.title_page || localFile.titlePage || {};

          // We also need to construct a fake multilingual entry so the server receives valid fileLanguages
          multilingualContent.push({
            languageCode: localFile.default_language || 'en',
            content: actualContent,
            titlePage: actualTitlePage,
            pinnedLocations: localFile.pinned_locations || {}
          });

          console.log('SyncService: Constructed fallback multilingual content from main file content');
        }
      }

      // Validate templateId - ensure it exists in the database
      // Valid templates: screenplay, youtube, instagram, podcast, stageplay, musicvideo
      const validTemplates = ['screenplay', 'youtube', 'instagram', 'podcast', 'stageplay', 'musicvideo'];
      let validatedTemplateId = localFile.template_id || 'screenplay';

      if (!validTemplates.includes(validatedTemplateId)) {
        console.warn(`SyncService: Invalid templateId "${validatedTemplateId}", falling back to "screenplay"`);
        validatedTemplateId = 'screenplay';
      }

      const fileData = {
        id: localFile.id, // Send client-generated ID to server
        projectId: localFile.project_id, // CRITICAL: Include project ID
        name: localFile.name,
        content: actualContent,
        titlePage: actualTitlePage,
        templateId: validatedTemplateId,
        templateData: localFile.template_data || {},
        defaultLanguage: localFile.default_language || 'en',
        availableLanguages: localFile.available_languages || ['en'],
        // Pinned locations are ONLY stored in file_languages, not in main files table
        fileLanguages: multilingualContent // Include multilingual content with annotations AND pinned locations
      };

      // Debug logging to see what's being uploaded
      console.log('SyncService: Uploading file data:', {
        id: localFile.id,
        projectId: localFile.project_id,
        name: localFile.name,
        mainContentLength: (localFile.content || '').length,
        actualContentLength: actualContent.length,
        contentPreview: actualContent.substring(0, 100),
        hasContent: !!actualContent,
        templateId: localFile.template_id,
        multilingualCount: multilingualContent.length
      });

      let response;
      let serverFile;

      try {
        // First, try to check if file exists on server
        const _checkResponse = await apiClient.get(`/api/files/${localFile.id}`);

        // If file exists, use PUT to update
        console.log('SyncService: File exists on server, using PUT to update:', localFile.name);

        const putData = {
          name: fileData.name,
          projectId: fileData.projectId, // CRITICAL: Include projectId in PUT updates!
          content: fileData.content,
          titlePage: fileData.titlePage,
          templateData: fileData.templateData,
          defaultLanguage: fileData.defaultLanguage,
          availableLanguages: fileData.availableLanguages
          // Pinned locations are ONLY stored in file_languages, not in main files table
        };

        console.log('SyncService: PUT request data:', {
          contentLength: putData.content.length,
          contentPreview: putData.content.substring(0, 100),
          hasContent: !!putData.content
        });

        response = await apiClient.put(`/api/files/${localFile.id}`, putData);
        // apiClient already unwraps response.data, so response IS the file object
        serverFile = response.file || response;
      } catch (checkError) {
        if (checkError.status === 404 || checkError.response?.status === 404) {
          // File doesn't exist on server, use POST to create
          console.log('SyncService: File not found on server, using POST to create:', localFile.name);
          response = await apiClient.post('/api/files', fileData);
          serverFile = response.file || response;
        } else {
          // Some other error occurred during check
          throw checkError;
        }
      }

      // Update local file with sync info using SERVER timestamps
      const serverTimestamp = new Date(serverFile.updatedAt || serverFile.serverUpdatedAt);
      await screenwriterDB.updateFile(localFile.id, {
        version: serverFile.version,
        last_synced: serverTimestamp,
        last_synced_at: serverTimestamp, // Use server timestamp, not local time
        // DON'T update last_modified - keep the actual content timestamp. Must be
        // passed explicitly (not just omitted) -- screenwriterDB.updateFile()
        // stamps last_modified to "now" by default whenever a caller doesn't
        // supply it, which happens to be strictly AFTER serverTimestamp on every
        // single call (server generates serverTimestamp, then this write runs on
        // the client once the response comes back). Omitting it here silently
        // made every file look locally-modified-after-sync immediately following
        // a real, successful upload -- confirmed live as the cause of "sync now"
        // never clearing the not-synced indicator no matter how many times it ran.
        last_modified: localFile.last_modified,
      });

      console.log(`SyncService: Updated sync timestamps for ${localFile.name} to ${serverTimestamp.toISOString()}`);

      console.log('SyncService: File synced to server with client ID:', localFile.name, localFile.id);

      // Deliberately no version-history save here. This used to independently
      // commit a version from `multilingualContent` (an IndexedDB read taken at
      // whatever moment this sync pass happened to run) — a second, uncoordinated
      // source of truth alongside ProseMirrorEditor's own live-state autosave
      // versioning (handleAutoSave -> scheduleVersionCommit). The two could race:
      // this sync pass firing mid-edit would snapshot stale/transitional content
      // that was never actually shown to the user, producing spurious non-monotonic
      // entries in version history (content duplication complaints traced to this).
      // Real version propagation across devices already happens via actual git
      // commits (gitService.push()/pull(), called from sync() below) — a file
      // content sync to the server doesn't need to also mint its own version.

      // After successful server upload, also save locally
      try {
        const syncMode = this.getSyncMode?.() || 'both';
        if (syncMode === 'local' || syncMode === 'both') {
          await this.saveToLocal(localFile);
        }
      } catch (localSaveErr) {
        console.warn('Local save after upload failed (non-fatal):', localSaveErr);
      }

      // Dispatch event to refresh UI even after upload - content might have been updated
      document.dispatchEvent(new CustomEvent('fileUpdatedFromServer', {
        detail: { fileId: localFile.id, fileName: localFile.name }
      }));
    } catch (error) {
      console.error('SyncService: File upload error:', error);
      throw error;
    }
  }

  // Delete file from local IndexedDB
  async deleteFileFromLocal(fileId) {
    try {
      console.log('SyncService: Deleting local file because it was removed from server:', fileId);
      await screenwriterDB.deleteFile(fileId);
      
      // Dispatch event to notify UI that file was deleted
      document.dispatchEvent(new CustomEvent('fileDeletedFromServer', {
        detail: { fileId: fileId, fileName: 'Deleted file' }
      }));
    } catch (error) {
      console.error('SyncService: Error deleting local file:', error);
    }
  }

  // Download server file to local - simplified without dual ID mapping
  async downloadFileFromServer(serverFile) {
    try {
      const response = await apiClient.get(`/api/files/${serverFile.id}`);
      const fileData = response?.file || response;
      if (!fileData?.id) {
        console.error('SyncService: File download returned no data for', serverFile.id);
        return;
      }

      // Check if file exists locally (now simple - just by ID)
      const existingFile = await screenwriterDB.getFile(fileData.id);

      if (existingFile) {
        // Check timestamps before overwriting
        const serverModified = new Date(fileData.updatedAt);
        const localModified = new Date(existingFile.last_modified);

        if (localModified >= serverModified) {
          console.log('SyncService: Skipping download - local version is same or newer:', fileData.name);
          // Update sync metadata only using SERVER timestamp -- last_modified must
          // be explicitly preserved here too, same reasoning as uploadFileToServer:
          // screenwriterDB.updateFile() defaults last_modified to "now" whenever
          // it's not passed, which would falsely re-flag this file as pending sync.
          await screenwriterDB.updateFile(existingFile.id, {
            last_synced: serverModified,
            last_synced_at: serverModified,
            version: fileData.version,
            last_modified: existingFile.last_modified
          });
          return;
        }

        console.log('SyncService: Downloading newer server content to existing file:', fileData.name);

        // Update existing file with server data
        const serverTimestamp = new Date(fileData.updatedAt);
        await screenwriterDB.updateFile(existingFile.id, {
          name: fileData.name,
          // Always sync project_id from server — local may have null if file was
          // created before the project association was saved server-side.
          ...(fileData.projectId && { project_id: fileData.projectId }),
          template_id: fileData.templateId,
          template_data: fileData.templateData,
          title_page: fileData.titlePage,
          content: fileData.content,
          // Pinned locations are ONLY in file_languages, not in main files table
          default_language: fileData.defaultLanguage,
          available_languages: fileData.availableLanguages,
          last_modified: serverTimestamp, // Use server timestamp for downloaded content
          version: fileData.version,
          last_synced: serverTimestamp,
          last_synced_at: serverTimestamp
        });
      } else {
        console.log('SyncService: Creating new file from server:', fileData.name);

        // Create new file with server ID
        const localFileData = {
          id: fileData.id, // Use server ID directly
          project_id: fileData.projectId,
          name: fileData.name,
          template_id: fileData.templateId,
          template_data: fileData.templateData,
          title_page: fileData.titlePage,
          content: fileData.content,
          // Pinned locations are ONLY in file_languages, not in main files table
          default_language: fileData.defaultLanguage,
          available_languages: fileData.availableLanguages,
          created: new Date(fileData.createdAt),
          last_modified: new Date(fileData.updatedAt), // Use server timestamp
          version: fileData.version,
          // Both fields, matching the other two branches in this function --
          // a file that just came straight from the server (this is the path
          // for a file that exists on the server but has no local row yet at
          // all, e.g. a fresh device/browser or a file created elsewhere) is
          // by definition in sync at this instant. last_synced_at specifically
          // is what syncFiles()'s own conflict-detection reads (localFile.
          // last_synced_at, not last_synced) to decide whether a file was ever
          // synced -- leaving it unset here made a freshly-downloaded file look
          // "never synced" on the very next sync run, and made the sidebar's
          // cloud-sync icon depend on falling back to last_synced instead of
          // the two fields agreeing the way they do everywhere else.
          last_synced: new Date(),
          last_synced_at: new Date()
        };

        await screenwriterDB.upsertFile(localFileData);
      }

      // Sync multilingual content
      if (fileData.fileLanguages) {
        for (const langData of fileData.fileLanguages) {
          await screenwriterDB.saveFileInLanguage(
            fileData.id,
            langData.languageCode,
            langData.content,
            langData.titlePage,
            {
              annotations: langData.annotations || {},
              pinned_locations: langData.pinnedLocations || {},
              last_modified: new Date(fileData.updatedAt)
            }
          );

          // Also save annotations to the ANNOTATIONS table for display
          if (langData.annotations) {
            for (const [elementId, elementAnnotations] of Object.entries(langData.annotations)) {
              if (Array.isArray(elementAnnotations)) {
                for (const annotation of elementAnnotations) {
                  try {
                    // Check if annotation already exists in table
                    const existing = await screenwriterDB.getAnnotations(fileData.id, langData.languageCode);
                    const exists = existing.find(a => a.id === annotation.id);

                    if (!exists) {
                      await screenwriterDB.addAnnotation({
                        id: annotation.id,
                        file_id: fileData.id,
                        language_code: langData.languageCode,
                        element_id: elementId,
                        type: annotation.type,
                        content: annotation.content,
                        audio_data: annotation.audio_data,
                        duration: annotation.duration,
                        user_name: annotation.user_name,
                        created: new Date(annotation.created || Date.now()),
                        last_modified: new Date(annotation.created || Date.now())
                      });
                    }
                  } catch (error) {
                    console.warn('Could not sync annotation to table:', error);
                  }
                }
              }
            }
          }
        }
      } else if (fileData.defaultLanguage && fileData.content) {
        // Create default language entry if none exists
        // NOTE: Pinned locations should come from fileLanguages, not from fileData
        await screenwriterDB.saveFileInLanguage(
          fileData.id,
          fileData.defaultLanguage,
          fileData.content,
          fileData.titlePage,
          {
            annotations: {},
            pinned_locations: {}, // No pinned locations from main fileData
            last_modified: new Date(fileData.updatedAt)
          }
        );
      }

      // Annotations will be synced through file language data only

      console.log('SyncService: File downloaded/updated from server:', fileData.name);

      // After successful server download, also save locally
      try {
        const syncMode = this.getSyncMode?.() || 'both';
        if (syncMode === 'local' || syncMode === 'both') {
          await this.saveToLocal(fileData);
        }
      } catch (localSaveErr) {
        console.warn('Local save after download failed (non-fatal):', localSaveErr);
      }

      // Dispatch event to refresh UI - the content is now updated locally with server data
      document.dispatchEvent(new CustomEvent('fileUpdatedFromServer', {
        detail: { fileId: fileData.id, fileName: fileData.name }
      }));

    } catch (error) {
      console.error('SyncService: File download error:', error);
    }
  }

  // Sync file languages with timestamp-based conflict resolution
  async syncFileLanguages(localFiles) {
    if (!this.authToken || !localFiles || localFiles.length === 0) {
      console.log('SyncService: Skipping file languages sync - no files or not authenticated');
      return;
    }

    // Filter out production files
    const filteredFiles = localFiles.filter(f => {
      if (f.template_id && typeof f.template_id === 'string' && (
        f.template_id.startsWith('production') ||
        f.template_id === 'callsheet' ||
        f.template_id === 'shootingschedule' ||
        f.template_id === 'budget' ||
        f.template_id === 'contactlist' ||
        f.template_id === 'locationlist' ||
        f.template_id === 'equipmentlist'
      )) {
        console.log('SyncService: Skipping language sync for production file:', f.name);
        return false;
      }
      return true;
    });

    if (filteredFiles.length === 0) {
      console.log('SyncService: No non-production files to sync languages');
      return;
    }

    try {
      const fileIds = filteredFiles.map(f => f.id);

      // Get server file languages with timestamps
      const response = await apiClient.post('/api/files/languages/sync', { fileIds });
      const serverLanguages = response?.fileLanguages || {};

      const languageUpdates = [];
      const languageDownloads = [];

      // Check each local file for multilingual content
      for (const localFile of filteredFiles) {
        const fileId = localFile.id;
        const serverFileLanguages = serverLanguages[fileId] || [];

        // Get local file languages for this file
        if (localFile.available_languages) {
          for (const languageCode of localFile.available_languages) {
            try {
              const localLangData = await screenwriterDB.getFileInLanguage(fileId, languageCode);
              const serverLangData = serverFileLanguages.find(sl => sl.languageCode === languageCode);

              if (localLangData && serverLangData) {
                // Both exist - compare timestamps
                const localModified = new Date(localLangData.local_updated_at || localLangData.last_modified || 0);
                const serverModified = new Date(serverLangData.updatedAt);
                const lastSynced = localLangData.last_synced_at ? new Date(localLangData.last_synced_at) : null;

                const localChangedSinceSync = lastSynced ? localModified > lastSynced : true;
                const serverChangedSinceSync = lastSynced ? serverModified > lastSynced : true;

                if (localChangedSinceSync && serverChangedSinceSync) {
                  // Both sides changed: merge with git (recorded as a new version)
                  // instead of letting the newer timestamp overwrite the other
                  // side's edits. A file with live collaborators is left to
                  // real-time collaboration.
                  if (await this.hasActiveCollaborators(fileId)) {
                    console.log(`SyncService: Language conflict left to live collaboration (${languageCode} for ${localFile.name})`);
                  } else {
                    const versionHistoryService = (await import('./versionHistoryService.js')).default;
                    const merge = await versionHistoryService.mergeWithServer(
                      fileId,
                      localLangData.content,
                      serverLangData.content,
                      languageCode
                    );

                    if (merge.success) {
                      console.log(`SyncService: Language conflict merged with git (${languageCode} for ${localFile.name})`);
                      await screenwriterDB.saveFileInLanguage(
                        fileId,
                        languageCode,
                        merge.content,
                        localLangData.title_page || null,
                        {
                          annotations: localLangData.annotations || {},
                          pinned_locations: localLangData.pinned_locations || {}
                        }
                      );
                      languageUpdates.push({
                        fileId,
                        languageCode,
                        content: merge.content,
                        titlePage: localLangData.title_page,
                        annotations: localLangData.annotations || {},
                        pinnedLocations: localLangData.pinned_locations || {},
                        localUpdatedAt: new Date().toISOString()
                      });
                      if (merge.commit) {
                        const connectedUsers = await this._getConnectedUsers(fileId);
                        versionMergeInfoService
                          .tagMerge(fileId, merge.commit, languageCode, 'auto_merge', connectedUsers)
                          .catch(err => console.warn('SyncService: Failed to tag language merge commit:', err.message));
                      }
                    } else {
                      console.log(`SyncService: Language conflict needs manual resolution (${languageCode} for ${localFile.name})`);
                      this.storeConflictForResolution({
                        fileId,
                        fileName: localFile.name,
                        localContent: [{ language_code: languageCode, content: localLangData.content }],
                        serverContent: [{ language_code: languageCode, content: serverLangData.content }],
                        localModified,
                        serverModified,
                        mergeResults: [{ language_code: languageCode, ...merge }]
                      });
                    }
                  }
                } else if (localChangedSinceSync) {
                  // Only local changed - upload
                  languageUpdates.push({
                    fileId,
                    languageCode,
                    content: localLangData.content,
                    titlePage: localLangData.title_page,
                    annotations: localLangData.annotations || {},
                    pinnedLocations: localLangData.pinned_locations || {},
                    localUpdatedAt: localModified.toISOString()
                  });
                } else if (serverChangedSinceSync) {
                  // Only server changed - download
                  languageDownloads.push({
                    fileId,
                    languageCode,
                    serverData: serverLangData
                  });
                }
              } else if (localLangData) {
                // Only local exists - upload to server
                languageUpdates.push({
                  fileId,
                  languageCode,
                  content: localLangData.content,
                  titlePage: localLangData.title_page,
                  annotations: localLangData.annotations || {},
                  pinnedLocations: localLangData.pinned_locations || {},
                  localUpdatedAt: (new Date(localLangData.local_updated_at || localLangData.last_modified || Date.now())).toISOString()
                });
              } else if (serverLangData) {
                // Only server exists - download to local
                languageDownloads.push({
                  fileId,
                  languageCode,
                  serverData: serverLangData
                });
              }
            } catch (error) {
              console.warn(`SyncService: Error checking language ${languageCode} for file ${fileId}:`, error);
            }
          }
        }

        // Check for server languages not in local available_languages
        for (const serverLang of serverFileLanguages) {
          if (!localFile.available_languages || !localFile.available_languages.includes(serverLang.languageCode)) {
            languageDownloads.push({
              fileId,
              languageCode: serverLang.languageCode,
              serverData: serverLang
            });
          }
        }
      }

      // Upload language updates (check for active collaboration first)
      if (languageUpdates.length > 0) {
        // Group updates by fileId to check collaboration
        const fileIds = [...new Set(languageUpdates.map(update => update.fileId))];
        const filteredUpdates = [];

        for (const fileId of fileIds) {
          const hasActiveCollabs = await this.hasActiveCollaborators(fileId);
          if (hasActiveCollabs) {
            console.log(`SyncService: Skipping language uploads for file ${fileId} - active collaboration detected`);
            continue;
          }

          // Add all updates for this file
          const fileUpdates = languageUpdates.filter(update => update.fileId === fileId);
          filteredUpdates.push(...fileUpdates);
        }

        if (filteredUpdates.length > 0) {
          console.log(`SyncService: Uploading ${filteredUpdates.length} language updates (${languageUpdates.length - filteredUpdates.length} skipped due to active collaboration)`);
          await apiClient.put('/api/files/languages/sync', { languageUpdates: filteredUpdates });
        } else {
          console.log('SyncService: All language updates skipped due to active collaboration');
        }
      }

      // Download language updates
      for (const download of languageDownloads) {
        await screenwriterDB.saveFileInLanguage(
          download.fileId,
          download.languageCode,
          download.serverData.content,
          download.serverData.titlePage,
          {
            annotations: download.serverData.annotations || {},
            pinned_locations: download.serverData.pinnedLocations || {},
            last_synced_at: new Date(),
            server_updated_at: new Date(download.serverData.updatedAt),
            last_modified: new Date(download.serverData.updatedAt) // Use server timestamp
          }
        );
      }

      if (languageUpdates.length > 0 || languageDownloads.length > 0) {
        console.log(`SyncService: File languages synced - ${languageUpdates.length} uploaded, ${languageDownloads.length} downloaded`);
      }

    } catch (error) {
      console.error('SyncService: File languages sync error:', error);
    }
  }

  async syncAnnotations() {
    if (!this.authToken) {
      console.log('SyncService: Skipping annotations sync - not authenticated');
      return;
    }

    try {
      console.log('SyncService: Syncing annotations from IndexedDB to server annotations table...');

      // Get all local files
      const localFiles = await screenwriterDB.listFiles();

      // Filter out production files
      const filteredFiles = localFiles.filter(f => {
        // Handle template_id as either string or object
        const templateId = typeof f.template_id === 'string'
          ? f.template_id
          : (typeof f.template_id === 'object' && f.template_id !== null
            ? f.template_id.id
            : null);

        if (templateId && (
          templateId.startsWith('production') ||
          templateId === 'callsheet' ||
          templateId === 'shootingschedule' ||
          templateId === 'budget' ||
          templateId === 'contactlist' ||
          templateId === 'locationlist' ||
          templateId === 'equipmentlist'
        )) {
          console.log('SyncService: Skipping annotation sync for production file:', f.name);
          return false;
        }
        return true;
      });

      for (const file of filteredFiles) {
        try {
          // Get file languages with annotations
          const fileLanguages = await screenwriterDB.getFileLanguages(file.id);

          for (const langData of fileLanguages || []) {
            if (langData.annotations && Object.keys(langData.annotations).length > 0) {
              // Extract all annotations from the nested structure
              const annotationsToSync = [];
              let needsUpdate = false;

              Object.entries(langData.annotations).forEach(([elementId, elementAnnotations]) => {
                if (Array.isArray(elementAnnotations)) {
                  elementAnnotations.forEach(annotation => {
                    // Only sync annotations that haven't been synced yet
                    if (!annotation.synced_to_server) {
                      const annotationData = {
                        id: annotation.id, // Now using proper UUIDs
                        elementId: String(elementId),
                        annotationType: annotation.anchorType || 'note',
                        languageCode: langData.language_code || 'en',
                        content: annotation.content || '',
                        positionData: {
                          selectors: annotation.selectors,
                          selectionRange: annotation.selectionRange, // SEND SELECTION RANGE TO SERVER
                          selectedText: annotation.selectedText, // SEND SELECTED TEXT TO SERVER
                          montage: annotation.anchorType === 'montage' ? annotation.montage : undefined,
                          montageTitle: annotation.anchorType === 'montage' ? annotation.title : undefined
                        },
                        _elementId: elementId, // Keep track for updating IndexedDB
                        _originalAnnotation: annotation // Keep reference for updating
                      };

                      // Only include voice-specific fields for voice annotations
                      if (annotation.anchorType === 'voice') {
                        if (annotation.voiceRecordingS3Key) {
                          annotationData.voiceRecordingS3Key = annotation.voiceRecordingS3Key;
                        }
                        if (annotation.duration != null) {
                          annotationData.voiceDuration = annotation.duration;
                        }
                      }

                      console.log('📤 Syncing annotation with selection to server:', {
                        id: annotationData.id,
                        hasSelectionRange: !!annotation.selectionRange,
                        selectedText: annotation.selectedText
                      });

                      annotationsToSync.push(annotationData);
                    }
                  });
                }
              });

              if (annotationsToSync.length > 0) {
                console.log(`SyncService: Syncing ${annotationsToSync.length} unsynced annotations for file ${file.id} (${langData.language_code})`);

                // Sync each annotation
                for (const annotation of annotationsToSync) {
                  try {
                    // Remove internal tracking fields before sending to server
                    const { _elementId, _originalAnnotation, ...annotationToSend } = annotation;

                    await apiClient.post(`/api/files/${file.id}/annotations`, annotationToSend);
                    console.log(`✅ Synced annotation ${annotation.id} to server`);

                    // Mark as synced in IndexedDB
                    annotation._originalAnnotation.synced_to_server = true;
                    needsUpdate = true;
                  } catch (error) {
                    console.warn(`Failed to sync annotation ${annotation.id}:`, error.response?.data || error.message);
                  }
                }

                // Update IndexedDB with synced flags
                if (needsUpdate) {
                  await screenwriterDB.saveFileInLanguage(
                    file.id,
                    langData.language_code,
                    null,
                    null,
                    {
                      annotations: langData.annotations
                    }
                  );
                  console.log(`✅ Updated sync flags in IndexedDB for file ${file.id}`);
                }
              }
            }
          }
        } catch (error) {
          console.warn(`SyncService: Error syncing annotations for file ${file.id}:`, error);
        }
      }

      console.log('SyncService: Annotations sync completed');
    } catch (error) {
      console.error('SyncService: Annotations sync error:', error);
    }
  }

  // Push ONLY the given editor preference keys to the server (PATCH). Returns
  // true when the keys no longer need pushing: accepted, or rejected by the
  // server's registry (retrying a poisoned key forever helps nobody -- the
  // provider's next refetch restores the server's value). Returns false only when
  // the push should be retried (not signed in, offline, or a transient failure).
  // Pulling server values back into the app is PreferencesStateProvider's job.
  async syncPreferences(keys) {
    if (!this.authToken) {
      console.log('SyncService: Skipping preference sync - not authenticated');
      return false;
    }
    if (!Array.isArray(keys) || keys.length === 0) return true;

    try {
      const blob = JSON.parse(localStorage.getItem('screenwriter_preferences') || '{}');
      const changes = {};
      keys.forEach((key) => {
        if (blob[key] !== undefined) changes[`editor.${key}`] = blob[key];
      });
      if (Object.keys(changes).length === 0) return true;

      await apiClient.patch('/api/preferences', changes);
      return true;
    } catch (error) {
      if (error.response?.status === 400) {
        console.warn('SyncService: Server rejected preference change:', error.response.data?.details || error.response.data?.error);
        return true;
      }
      // Preferences sync is not critical - log, keep the keys dirty, retry later
      console.warn('SyncService: Preference sync error:', error.response?.data?.error || error.message);
      return false;
    }
  }

  // Sync subscription information
  async syncSubscription() {
    // Don't sync if not authenticated
    if (!this.authToken) {
      console.log('SyncService: Skipping subscription sync - not authenticated');
      return;
    }

    try {
      // Get current subscription from server
      const response = await apiClient.get('/api/subscriptions/current');
      const subscriptionData = response;

      // Store subscription data in localStorage for offline access
      localStorage.setItem('currentSubscription', JSON.stringify({
        ...subscriptionData,
        lastSynced: new Date().toISOString()
      }));

      console.log('SyncService: Subscription synced:', subscriptionData.plan);

      // Dispatch event for UI to update
      document.dispatchEvent(new CustomEvent('subscriptionUpdated', {
        detail: subscriptionData
      }));

    } catch (error) {
      // Subscription sync is not critical - log but don't fail
      console.warn('SyncService: Subscription sync error:', error.response?.data?.error || error.message);
    }
  }

  // Sync token usage to server
  async syncTokenUsage() {
    if (!this.authToken) {
      console.log('SyncService: Skipping token usage sync - not authenticated');
      return;
    }

    try {
      // Get pending token usage records from localStorage
      const pendingTokens = JSON.parse(localStorage.getItem('pendingTokenSync') || '[]');

      if (pendingTokens.length === 0) {
        console.log('SyncService: No token usage data to sync');
        return;
      }

      // Format tokens for server API (expects array of token entries)
      const formattedTokens = pendingTokens.map(token => ({
        serviceType: token.serviceType,
        tokensUsed: token.tokensUsed,
        creditsUsed: token.creditsUsed,
        metadata: token.metadata || {},
        timestamp: token.timestamp
      }));

      // Send token usage to server
      await apiClient.post('/api/usage/sync-tokens', {
        tokens: formattedTokens
      });

      console.log(`SyncService: Synced ${formattedTokens.length} token usage records to server`);

      // Clear synced tokens from localStorage
      localStorage.setItem('pendingTokenSync', JSON.stringify([]));
    } catch (error) {
      // Token sync is not critical - log but don't fail
      console.warn('SyncService: Token usage sync error:', error.response?.data?.error || error.message);
    }
  }

  // Sync chat history (bidirectional: upload + download)
  async syncChatHistory() {
    if (!this.authToken) {
      console.log('SyncService: Skipping chat history sync - not authenticated');
      return;
    }

    try {
      // ===== PART 1: UPLOAD unsynced local chats to server =====
      const unsyncedChats = await screenwriterDB.getUnsyncedChatHistories();

      if (unsyncedChats.length > 0) {
        console.log(`SyncService: Uploading ${unsyncedChats.length} unsynced chat messages to server`);
      }

      if (unsyncedChats.length > 0) {
        // Format chats for server API
        const formattedChats = unsyncedChats.map(chat => {
          const tokensInput = chat.tokens_input || 0;
          const tokensOutput = chat.tokens_output || 0;
          const totalTokens = tokensInput + tokensOutput;

          return {
            sessionId: chat.sessionId || '',
            fileId: chat.fileId || null, // ✅ FIXED: Include fileId
            messageType: chat.messageType || 'user_input',
            content: chat.content || '',
            mode: chat.mode || 'chat',
            messageMode: chat.messageMode || chat.mode, // ✅ FIXED: Include messageMode
            template: chat.template || null,
            category: chat.category || 'screenplay', // ✅ FIXED: Include category
            formattedElements: chat.formattedElements || null,
            artifactType: chat.artifactType || null,
            artifactTitle: chat.artifactTitle || null,
            steps: chat.steps || null,
            originalText: chat.originalText || null,
            contextData: chat.contextData || null,
            messageContext: chat.messageContext || null,
            tokens_input: tokensInput,
            tokens_output: tokensOutput,
            tokensUsed: totalTokens,
            creditsUsed: chat.creditsUsed || 0,
            cost_usd: chat.cost_usd || 0,
            model_used: chat.model_used || null, // ✅ FIXED: Include model_used
            sessionMetadata: chat.sessionMetadata || null, // ✅ FIXED: Include sessionMetadata
            createdAt: chat.createdAt || new Date().toISOString()
          };
        });

        // Upload to server
        await apiClient.post('/api/chat-history/sync', {
          chats: formattedChats
        });

        console.log(`SyncService: ✅ Uploaded ${formattedChats.length} chat messages to server`);

        // Mark uploaded chats as synced
        const uploadedChatIds = unsyncedChats.map(chat => chat.id);
        await screenwriterDB.markChatHistoriesAsSynced(uploadedChatIds);
        console.log(`SyncService: ✅ Marked ${uploadedChatIds.length} chats as synced in IndexedDB`);
      }

      // ===== PART 2: DOWNLOAD server chats that don't exist locally =====
      // Get all chat sessions from server
      const sessionsResponse = await apiClient.get('/api/chat-history/sessions');
      const serverSessions = sessionsResponse.sessions || [];

      if (serverSessions.length === 0) {
        console.log('SyncService: No server chat sessions to download');
        return;
      }


      // Download each session's messages
      let totalDownloaded = 0;
      for (const session of serverSessions) {
        try {
          // Get messages for this session from server
          const sessionResponse = await apiClient.get(`/api/chat-history/session/${session.sessionId}`);
          const serverMessages = sessionResponse.messages || [];

          // Get existing local messages for this session
          const localMessages = await screenwriterDB.getChatHistories(session.sessionId);
          const localMessageMap = new Map(localMessages.map(msg => [msg.server_id || msg.id, msg]));

          // Download messages that don't exist locally
          for (const serverMsg of serverMessages) {
            // Check if this message already exists locally (by server_id or matching content+timestamp)
            const existsLocally = localMessageMap.has(serverMsg.id) ||
              localMessages.some(local =>
                local.content === serverMsg.content &&
                Math.abs(new Date(local.createdAt) - new Date(serverMsg.createdAt)) < 1000
              );

            if (!existsLocally) {
              // Download and save to IndexedDB
              await screenwriterDB.saveChatHistory({
                server_id: serverMsg.id, // Store server ID for future reference
                userId: serverMsg.userId,
                sessionId: serverMsg.sessionId,
                messageType: serverMsg.messageType,
                content: serverMsg.content,
                mode: serverMsg.mode,
                template: serverMsg.template,
                formattedElements: serverMsg.formattedElements,
                artifactType: serverMsg.artifactType || null,
                artifactTitle: serverMsg.artifactTitle || null,
                originalText: serverMsg.originalText,
                contextData: serverMsg.contextData,
                messageContext: serverMsg.messageContext,
                tokens_input: serverMsg.tokensInput || 0,
                tokens_output: serverMsg.tokensOutput || 0,
                cost_usd: serverMsg.costUsd || 0,
                creditsUsed: serverMsg.creditsUsed || 0,
                synced: true, // Already on server, so mark as synced
                createdAt: new Date(serverMsg.createdAt)
              });
              totalDownloaded++;
            }
          }
        } catch (sessionError) {
          console.warn(`Failed to download session ${session.sessionId}:`, sessionError.message);
        }
      }

      if (totalDownloaded > 0) {
        console.log(`SyncService: ✅ Downloaded ${totalDownloaded} new chat messages from server`);
      } else {
        console.log('SyncService: No new chat messages to download from server');
      }

      // Update last chat sync timestamp
      localStorage.setItem('lastChatSync', new Date().toISOString());
    } catch (error) {
      console.warn('SyncService: Chat history sync error:', error.response?.data?.error || error.message);
    }
  }

  // Sync document outlines
  async syncDocumentOutlines() {
    if (!this.authToken) {
      console.log('SyncService: Skipping document outlines sync - not authenticated');
      return;
    }

    try {
      // Get all local document outlines
      const localFiles = await screenwriterDB.listFiles();
      const localOutlinesMap = new Map();

      // Collect all local outlines grouped by file
      for (const file of localFiles) {
        try {
          const outlines = await screenwriterDB.listDocumentOutlines(file.id);
          if (outlines && outlines.length > 0) {
            localOutlinesMap.set(file.id, outlines);
          }
        } catch (error) {
          console.warn(`Error loading outlines for file ${file.id}:`, error);
        }
      }

      if (localFiles.length === 0) {
        console.log('SyncService: No local files to sync outlines for');
        return;
      }

      // Ask the server about EVERY local file, not just ones that already
      // have local outlines -- a file whose only outlines were generated
      // server-side (tarr-agent via MCP) and never synced down before would
      // otherwise never be included here, silently hiding those beats forever.
      const fileIds = localFiles.map((f) => f.id);
      const serverOutlinesResponse = await apiClient.post('/api/document-outlines/sync', { fileIds });
      const serverOutlinesMap = new Map();

      // Group server outlines by fileId
      (serverOutlinesResponse.data || []).forEach(outline => {
        if (!serverOutlinesMap.has(outline.fileId)) {
          serverOutlinesMap.set(outline.fileId, []);
        }
        serverOutlinesMap.get(outline.fileId).push(outline);
      });

      // Sync outlines for each file
      for (const fileId of fileIds) {
        const localOutlines = localOutlinesMap.get(fileId) || [];
        const serverOutlines = serverOutlinesMap.get(fileId) || [];
        await this.syncOutlinesForFile(fileId, localOutlines, serverOutlines);
      }

      console.log('SyncService: Document outlines synced successfully');
    } catch (error) {
      console.error('SyncService: Document outlines sync error:', error);
    }
  }

  // Helper method to sync outlines for a specific file
  async syncOutlinesForFile(fileId, localOutlines, serverOutlines) {
    try {
      const localOutlinesMap = new Map(localOutlines.map(outline => [outline.id, outline]));
      const serverOutlinesMap = new Map(serverOutlines.map(outline => [outline.id, outline]));

      const outlinesToUpload = [];
      const outlinesToCreate = [];
      const outlinesToDownload = [];

      // Check local outlines against server
      for (const [outlineId, localOutline] of localOutlinesMap) {
        const serverOutline = serverOutlinesMap.get(outlineId);
        if (!serverOutline) {
          // Local outline doesn't exist on server - create
          outlinesToCreate.push(localOutline);
        } else {
          // Compare timestamps
          const localModified = new Date(localOutline.updatedAt);
          const serverModified = new Date(serverOutline.updatedAt);

          if (localModified > serverModified) {
            // Local is newer - upload
            outlinesToUpload.push(localOutline);
          } else if (serverModified > localModified) {
            // Server is newer - download
            outlinesToDownload.push(serverOutline);
          }
        }
      }

      // Check for server outlines not in local
      for (const [outlineId, serverOutline] of serverOutlinesMap) {
        if (!localOutlinesMap.has(outlineId)) {
          // Server outline doesn't exist locally - download
          outlinesToDownload.push(serverOutline);
        }
      }

      // Create new local outlines on server (server uses upsert)
      for (const outline of outlinesToCreate) {
        try {
          // Ensure content is always a string
          let contentString = '';
          if (typeof outline.content === 'string') {
            contentString = outline.content;
          } else if (Array.isArray(outline.content)) {
            contentString = outline.content.join('\n');
          } else if (outline.content && typeof outline.content === 'object') {
            contentString = JSON.stringify(outline.content, null, 2);
          } else if (outline.content) {
            contentString = String(outline.content);
          }

          const createData = {
            id: outline.id, // Include ID - server handles upsert
            fileId: outline.fileId,
            templateId: outline.templateId,
            outlineType: outline.outlineType,
            title: outline.title,
            content: contentString,
            aiGenerated: outline.aiGenerated || false,
            metadata: outline.metadata || {}
          };

          // Only include projectId if it's a valid string
          if (outline.projectId) {
            createData.projectId = outline.projectId;
          }

          // Only include aiPromptUsed if it's a valid string and not "none"
          if (outline.aiPromptUsed && outline.aiPromptUsed !== 'none') {
            createData.aiPromptUsed = outline.aiPromptUsed;
          }

          // Server will upsert - create if doesn't exist, update if it does
          await apiClient.post('/api/document-outlines', createData);
          console.log(`Synced outline ${outline.id} to server`);
        } catch (error) {
          console.warn(`Failed to sync outline ${outline.id}:`, error);
        }
      }

      // Update existing local outlines on server
      for (const outline of outlinesToUpload) {
        try {
          // Ensure content is always a string
          let contentString = '';
          if (typeof outline.content === 'string') {
            contentString = outline.content;
          } else if (Array.isArray(outline.content)) {
            contentString = outline.content.join('\n');
          } else if (outline.content && typeof outline.content === 'object') {
            contentString = JSON.stringify(outline.content, null, 2);
          } else if (outline.content) {
            contentString = String(outline.content);
          }

          const updateData = {
            title: outline.title,
            content: contentString,
            metadata: outline.metadata || {}
          };

          // Only include aiPromptUsed if it's a valid string and not "none"
          if (outline.aiPromptUsed && outline.aiPromptUsed !== 'none') {
            updateData.aiPromptUsed = outline.aiPromptUsed;
          }

          await apiClient.put(`/api/document-outlines/${outline.id}`, updateData);
        } catch (error) {
          console.warn(`Failed to upload outline ${outline.id}:`, error);
        }
      }

      // Download new/updated server outlines
      for (const serverOutline of outlinesToDownload) {
        try {
          const outlineData = {
            id: serverOutline.id,
            projectId: serverOutline.projectId,
            fileId: serverOutline.fileId,
            templateId: serverOutline.templateId,
            outlineType: serverOutline.outlineType,
            title: serverOutline.title,
            content: serverOutline.content,
            aiGenerated: serverOutline.aiGenerated || false,
            aiPromptUsed: serverOutline.aiPromptUsed,
            metadata: serverOutline.metadata || {},
            createdAt: new Date(serverOutline.createdAt),
            updatedAt: new Date(serverOutline.updatedAt)
          };

          // Always check if outline already exists locally to prevent duplicates
          let existingOutline = null;
          try {
            existingOutline = await screenwriterDB.getDocumentOutline(serverOutline.id);
          } catch (error) {
            // Outline doesn't exist, which is fine
            console.log(`Outline ${serverOutline.id} doesn't exist locally, will create`);
          }

          if (existingOutline) {
            // Update existing outline
            console.log(`Updating existing outline ${serverOutline.id}`);
            await screenwriterDB.updateDocumentOutline(serverOutline.id, outlineData);
          } else {
            // Create new outline only if it doesn't exist
            console.log(`Creating new outline ${serverOutline.id}`);
            await screenwriterDB.createDocumentOutline(outlineData);
          }
        } catch (error) {
          console.warn(`Failed to download outline ${serverOutline.id}:`, error);
        }
      }

      if (outlinesToUpload.length > 0 || outlinesToDownload.length > 0) {
        console.log(`Synced outlines for file ${fileId}: ${outlinesToUpload.length} uploaded, ${outlinesToDownload.length} downloaded`);
      }
    } catch (error) {
      console.error(`Error syncing outlines for file ${fileId}:`, error);
    }
  }

  // Apply server change to local database
  async applyServerChange(change) {
    try {
      const { fileId, operation, data, timestamp } = change;

      switch (operation) {
        case 'create':
          // For create operations, use the data directly instead of making API call
          const localFileData = {
            id: fileId,
            project_id: data.projectId,
            name: data.name,
            template_id: data.templateId,
            title_page: data.titlePage || {},
            content: data.content || '',
            // Pinned locations are ONLY in file_languages, not in main files table
            default_language: data.defaultLanguage || 'en',
            available_languages: data.availableLanguages || ['en'],
            created: new Date(data.createdAt || timestamp),
            last_modified: new Date(timestamp),
            version: data.version || 1,
            last_synced: new Date()
          };
          await screenwriterDB.upsertFile(localFileData);
          break;

        case 'update':
          const existingFile = await screenwriterDB.getFile(fileId);
          if (existingFile) {
            // Check if server version is actually newer before updating
            // MUST check multilingual content timestamps, not just main file
            const serverModified = new Date(timestamp);
            let localModified = new Date(existingFile.last_modified);

            // Check multilingual content for more recent timestamps
            const multilingualContent = await screenwriterDB.getFileLanguages(fileId);
            if (multilingualContent && multilingualContent.length > 0) {
              const multilingualTimestamps = multilingualContent
                .map(lang => new Date(lang.last_modified || lang.updated))
                .filter(date => !isNaN(date.getTime()));

              if (multilingualTimestamps.length > 0) {
                const latestMultilingualTime = new Date(Math.max(...multilingualTimestamps));
                if (latestMultilingualTime > localModified) {
                  localModified = latestMultilingualTime;
                  console.log(`Using multilingual timestamp for update check: ${latestMultilingualTime.toISOString()}`);
                }
              }
            }

            if (serverModified > localModified) {
              console.log('SyncService: Updating file with newer server version:', data.name);
              await screenwriterDB.updateFile(fileId, {
                name: data.name,
                content: data.content,
                title_page: data.titlePage,
                template_id: data.templateId,
                template_data: data.templateData,
                // Pinned locations are ONLY in file_languages, not in main files table
                default_language: data.defaultLanguage,
                available_languages: data.availableLanguages,
                last_modified: new Date(timestamp),
                last_synced: new Date(),
                version: data.version || existingFile.version
              });
            } else {
              console.log('SyncService: Skipping update - local version is same or newer:', data.name);
              // Just update sync timestamp -- preserve last_modified explicitly
              // (same reasoning as uploadFileToServer/downloadFileFromServer:
              // screenwriterDB.updateFile() defaults it to "now" otherwise).
              await screenwriterDB.updateFile(fileId, {
                last_synced: new Date(),
                last_modified: existingFile.last_modified
              });
            }
          } else {
            console.log('SyncService: Creating new file from server:', data.name);
            // File doesn't exist locally, create it using upsert
            const localFileData = {
              id: fileId,
              project_id: data.projectId,
              name: data.name,
              template_id: data.templateId || 'screenplay',
              template_data: data.templateData || {},
              title_page: data.titlePage || {},
              content: data.content || '',
              // Pinned locations are ONLY in file_languages, not in main files table
              default_language: data.defaultLanguage || 'en',
              available_languages: data.availableLanguages || ['en'],
              created: new Date(data.createdAt || timestamp),
              last_modified: new Date(timestamp),
              version: data.version || 1,
              last_synced: new Date()
            };
            await screenwriterDB.upsertFile(localFileData);
          }
          break;

        case 'delete':
          await screenwriterDB.deleteFile(fileId);
          break;

        default:
          console.warn('SyncService: Unknown operation:', operation);
      }
    } catch (error) {
      console.error('SyncService: Error applying server change:', error);
    }
  }

  // Manually trigger sync
  async manualSync() {
    if (this.syncInProgress) {
      console.log('SyncService: Sync already in progress');
      return;
    }

    if (!this.isOnline) {
      console.log('SyncService: Cannot sync while offline');
      return;
    }

    if (!this.authToken) {
      console.log('SyncService: Cannot sync - not authenticated');
      return;
    }

    await this.performFullSync();
  }

  // Get sync status
  getSyncStatus() {
    return {
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress,
      lastSyncTime: this.lastSyncTime,
      lastSyncError: this.lastSyncError,
      authToken: !!this.authToken
    };
  }

  // Auth is cookie-based — refresh authenticated state from currentUser
  updateAuthToken(_token) {
    this.authToken = !!localStorage.getItem('currentUser');
  }

  // Set user information after authentication
  setUserInfo(user) {
    if (user && user.id) {
      // Update IndexedDB with proper user ID
      if (screenwriterDB.setUserId) {
        screenwriterDB.setUserId(user.id);
      }

      // Store user info for sync
      localStorage.setItem('currentUser', JSON.stringify(user));

      // Auth is cookie-based — user is now authenticated
      this.authToken = true;

      console.log('SyncService: User info updated', { userId: user.id });

      // Trigger sync after user authentication
      if (this.isOnline) {
        this.performFullSync();
      }
    }
  }

  // Sync version history
  async syncVersionHistory() {
    if (!this.authToken) {
      console.log('SyncService: Skipping version sync - not authenticated');
      return;
    }

    try {
      // Get versions that need syncing
      const versionsToSync = await screenwriterDB.getVersionsNeedingSync();

      if (versionsToSync.length === 0) {
        console.log('SyncService: No versions to sync');
        return;
      }

      console.log(`SyncService: Syncing ${versionsToSync.length} versions`);

      for (const version of versionsToSync) {
        try {
          await apiClient.post('/api/file-versions', version);
          await screenwriterDB.clearVersionSyncFlag(version.id);
          console.log(`✅ Synced version ${version.version} for file ${version.file_id}`);
        } catch (error) {
          console.error(`Failed to sync version ${version.id}:`, error.message);
        }
      }
    } catch (error) {
      console.error('SyncService: Version sync error:', error);
    }
  }

  // Handle authentication
  async authenticate() {
    this.authToken = !!localStorage.getItem('currentUser');
    return this.authToken;
  }

  // Re-point a chat's messages from an old session id to a new one, both
  // locally and on the server -- used when a project is created *from* an
  // already-in-progress chat (a random session id assigned before any
  // project existed), so messages already sent aren't orphaned under an id
  // nothing will look up again once the URL/session context moves to the
  // real project id. Best-effort on the server call (network/auth issues
  // shouldn't block project creation from completing); the local update
  // always runs so the open tab's own message list stays correct either way.
  async reassociateChatSession(oldSessionId, newSessionId) {
    if (!oldSessionId || !newSessionId || oldSessionId === newSessionId) return { updatedCount: 0 };

    let serverUpdatedCount = 0;
    try {
      const response = await apiClient.post('/api/chat-history/reassociate', { oldSessionId, newSessionId });
      serverUpdatedCount = response?.updatedCount || 0;
    } catch (error) {
      console.warn('SyncService: Failed to reassociate chat session on server (will retry on next sync):', error.message);
    }

    const localUpdatedCount = await screenwriterDB.reassociateChatSession(oldSessionId, newSessionId);
    console.log(`SyncService: Reassociated chat session ${oldSessionId} -> ${newSessionId} (server: ${serverUpdatedCount}, local: ${localUpdatedCount})`);
    return { updatedCount: Math.max(serverUpdatedCount, localUpdatedCount) };
  }
}

// Create singleton instance
const syncService = new SyncService();

export default syncService;

// Export class for testing
export { SyncService };
